import * as React from "react";
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Collapse,
  IconButton,
  TextField,
  Divider,
  InputAdornment,
  FormControl,
  CircularProgress,
  Alert,
  MenuItem,
  Pagination
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

// 🔧 ajusta la ruta si difiere
import { reportArriendosService } from "@/service/services/ReportArriendos.service";

const UI = {
  title: '#155172', // azul petróleo para títulos
  head: '#3f9bcc', // encabezado de tabla
  bg: '#E9E7E3', // fondo de la sección
  paper: '#ffffff', // cards/papers
  border: '#E5E7EB', // bordecito claro
};

// ----- helpers -----
const currency = (n: number) =>
  new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  }).format(n || 0);


function unwrap(resp) {
  // Si tu http client (axios) trae { data }, úsalo; si no, devuelve resp tal cual
  if (resp && typeof resp === "object" && resp !== null && "data" in resp) {
    return resp.data;
  }
  return resp;
}

function useDebounced(value, delay) {
  const [v, setV] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setV(value), delay || 300);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

// ----- fila expandible -----
function ExpandRow(props) {
  const {
    row, mode, details, isLoading, error, onToggle,
    detailsMeta, detailsPage, detailsPerPage,
    onDetailsPageChange, onDetailsPerPageChange
  } = props;
  const [open, setOpen] = React.useState(false);

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    onToggle(next);
  };

  return (
    <>
      <TableRow hover>
        <TableCell padding="checkbox">
          <IconButton size="small" onClick={handleToggle}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontWeight: 600 }}>{row.name}</TableCell>

        {mode === "producto" ? (
          <>
            <TableCell align="right">{row.cantArriendos}</TableCell>
            <TableCell align="right">{currency(row.facturado)}</TableCell>
            <TableCell align="right">{currency(row.pagado)}</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>
              {currency(row.pendiente)}
            </TableCell>
          </>
        ) : (
          <>
            <TableCell align="right">{row.cantArriendos}</TableCell>
            <TableCell align="right">{row.cantProductos || 0}</TableCell>
            <TableCell align="right">{currency(row.facturado)}</TableCell>
            <TableCell align="right">{currency(row.pagado)}</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>
              {currency(row.pendiente)}
            </TableCell>
          </>
        )}
      </TableRow>

      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ m: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Detalle de arriendos
              </Typography>

              {isLoading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 2 }}>
                  <CircularProgress size={20} />{" "}
                  <Typography variant="body2">Cargando detalle…</Typography>
                </Box>
              ) : error ? (
                <Alert severity="error">{error}</Alert>
              ) : (
                <Paper variant="outlined" sx={{ overflow: "hidden" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "action.hover" }}>
                        <TableCell>Fecha</TableCell>
                        {mode === "producto" ? (
                          <TableCell>Cliente</TableCell>
                        ) : (
                          <TableCell>Producto</TableCell>
                        )}
                        <TableCell align="right">Facturado</TableCell>
                        <TableCell align="right">Pagado</TableCell>
                        <TableCell align="right">No facturado</TableCell>
                        <TableCell align="right">Devengado</TableCell>
                        <TableCell align="right">Pendiente</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(details || []).map((d) => (
                        <TableRow key={(d.idarriendo || "") + "-" + (d.fecha || "")}>
                          <TableCell>{d.fecha}</TableCell>
                          {mode === "producto" ? (
                            <TableCell>{d.cliente_nombre}</TableCell>
                          ) : (
                            <TableCell>{d.producto_nombre}</TableCell>
                          )}
                          <TableCell align="right">{currency(d.facturado)}</TableCell>
                          <TableCell align="right">{currency(d.pagado)}</TableCell>
                          <TableCell align="right">{currency(d.no_facturado)}</TableCell>
                          <TableCell align="right">{currency(d.devengado)}</TableCell>
                          <TableCell align="right">{currency(d.pendiente)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {!!detailsMeta && (
                    <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                      <Pagination
                        size="small"
                        page={detailsPage || detailsMeta.page || 1}
                        count={detailsMeta.total_pages || 1}
                        onChange={(_, p) => onDetailsPageChange && onDetailsPageChange(p)}
                      />
                      <TextField
                        select
                        size="small"
                        label="Por página"
                        value={detailsPerPage || detailsMeta.per_page || 10}
                        onChange={(e) => onDetailsPerPageChange && onDetailsPerPageChange(Number(e.target.value))}
                      >
                        {[5, 10, 20, 50].map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                      </TextField>
                      <Box sx={{ ml: 'auto' }}>
                        <Typography variant="caption" color="text.secondary">
                          Mostrando {(details || []).length} de {detailsMeta.total || 0}.
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Paper>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// ----- vista principal -----
export default function GroupReport() {

  const [page, setPage] = React.useState(1);
  const [perPage, setPerPage] = React.useState(10);
  const [meta, setMeta] = React.useState({ total: 0, total_pages: 0, page: 1, per_page: 10 });

  const [range, setRange] = React.useState("mes"); // "mes" | "3m" | "custom"
  const [desde, setDesde] = React.useState("");
  const [hasta, setHasta] = React.useState("");
  const [mode, setMode] = React.useState("producto"); // "producto" | "cliente"
  const [query, setQuery] = React.useState("");

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(undefined);

  const [summary, setSummary] = React.useState({
    total: 0,
    pagado: 0,
    devengado: 0,
    pendiente: 0,
    n_arriendos: 0
  });
  const [items, setItems] = React.useState([]);

  const [detailsMap, setDetailsMap] = React.useState({});
  // shape por key: { loading, error?, rows?, meta?, page, perPage }
  const debouncedQuery = useDebounced(query, 350);

  const buildParams = React.useCallback(() => {
    const params: any = { mode: mode, page, per_page: perPage };
    if (range === "custom") {
      params.range = "custom";
      if (desde) params.from = desde;
      if (hasta) params.to = hasta;
    } else {
      params.range = range; // 'mes' | '3m'
    }
    if (debouncedQuery) params.q = debouncedQuery;
    return params;
  }, [mode, range, desde, hasta, debouncedQuery, page, perPage]);

  const loadGrouped = React.useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      const params = buildParams();
      const resp = await reportArriendosService.getGrouped(params);
      const payload = unwrap(resp);

      setSummary(payload.summary || { total: 0, pagado: 0, devengado: 0, pendiente: 0 });
      setItems(payload.items || []);

      const m = payload.meta || { total: 0, page: 1, per_page: perPage, total_pages: 0 };
      setMeta(m);
      if ((m.page ?? 1) !== page) setPage(m.page ?? 1);
      if ((m.per_page ?? perPage) !== perPage) setPerPage(m.per_page ?? perPage);
    } catch (e) {
      console.log(e)
      setSummary({ total: 0, pagado: 0, devengado: 0, pendiente: 0, n_arriendos: 0 });
      setItems([]);
      setMeta({ total: 0, page: 1, per_page: perPage, total_pages: 0 });
    } finally {
      setLoading(false);
    }
  }, [buildParams, page, perPage]);

  // Cuando cambian filtros, resetea cache y vuelve a página 1
  React.useEffect(() => {
    setDetailsMap({});
    setPage(1);
  }, [mode, range, desde, hasta, debouncedQuery]);

  // Haz el fetch cuando cambian filtros o la página/perPage
  React.useEffect(() => {
    loadGrouped();
  }, [mode, range, desde, hasta, debouncedQuery, page, perPage, loadGrouped]);

  const loadDetailsFor = React.useCallback(
    async (key: number, page: number = 1, perPageDet: number = 10) => {
      // marca loading y guarda la intención de page/perPage del detalle
      setDetailsMap((m: any) => ({
        ...m,
        [key]: { ...(m[key] || {}), loading: true, page, perPage: perPageDet },
      }));

      try {
        const base = buildParams(); // trae mode, rango, q, etc. (NO page/per_page globales)
        const params = { ...base, id: key, page, per_page: perPageDet };

        const resp = await reportArriendosService.getDetail(params);
        const payload = unwrap(resp);

        const meta = payload?.meta ?? {
          total: (payload?.rows || []).length,
          page,
          per_page: perPageDet,
          total_pages: 1,
        };

        setDetailsMap((m: any) => {
          const prev = m[key] || {};
          // evita re-render innecesario si nada cambió
          const samePage = (prev.page ?? 1) === (meta.page ?? page);
          const samePer = (prev.perPage ?? perPageDet) === (meta.per_page ?? perPageDet);
          const sameRows = Array.isArray(prev.rows) && Array.isArray(payload?.rows)
            ? prev.rows.length === payload.rows.length
            : false;

          const next = {
            ...prev,
            loading: false,
            rows: payload?.rows || [],
            meta,
            page: meta.page ?? page,
            perPage: meta.per_page ?? perPageDet,
          };

          if (samePage && samePer && sameRows) return m; // no cambies estado si es igual
          return { ...m, [key]: next };
        });
      } catch (e) {
        console.error(e);
        setDetailsMap((m: any) => ({
          ...m,
          [key]: { ...(m[key] || {}), loading: false, error: "No se pudo cargar el detalle" },
        }));
      }
    },
    [buildParams]
  );

  const handleDetailsPageChange = React.useCallback((key, newPage) => {
    setDetailsMap((m) => {
      const prev = m[key] || {};
      return { ...m, [key]: { ...prev, page: newPage } };
    });
    const curr = detailsMap[key];
    const per = (curr && curr.perPage) || 10;
    loadDetailsFor(key, newPage, per);
  }, [detailsMap, loadDetailsFor]);

  const handleDetailsPerPageChange = React.useCallback((key, newPerPage) => {
    setDetailsMap((m) => {
      const prev = m[key] || {};
      return { ...m, [key]: { ...prev, perPage: newPerPage, page: 1 } };
    });
    loadDetailsFor(key, 1, newPerPage);
  }, [loadDetailsFor]);


  return (
    <Box sx={{ bgcolor: "background.default", py: 3 }}>
      <Container maxWidth="lg">
        <Stack spacing={0.5} sx={{ mb: 2 }}>
          <Typography variant="h5" fontWeight={700} sx={{ color: UI.title }}>
            Reporte de arriendos — Totales por {mode === "producto" ? "Producto" : "Cliente"}
          </Typography>
        </Stack>

        {/* Resumen */}
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(5, 1fr)' },
            mb: 2,
            '& .summaryCard': {
              p: 2,
              bgcolor: UI.paper,
              border: `1px solid ${UI.border}`,
              boxShadow: 'none',
              borderRadius: 2,
            }
          }}
        >
          <Paper className="summaryCard">
            <Typography variant="caption" color="text.secondary"># Arriendos</Typography>
            <Typography variant="h6" sx={{ mt: 0.5 }}>{summary.n_arriendos}</Typography>
          </Paper>

          <Paper className="summaryCard">
            <Typography variant="caption" color="text.secondary">Facturado</Typography>
            <Typography variant="h6" sx={{ mt: 0.5 }}>{currency(summary.total)}</Typography>
          </Paper>

          <Paper className="summaryCard">
            <Typography variant="caption" color="text.secondary">Devengado</Typography>
            <Typography variant="h6" sx={{ mt: 0.5 }}>{currency(summary.devengado)}</Typography>
          </Paper>

          <Paper className="summaryCard">
            <Typography variant="caption" color="text.secondary">Pagado</Typography>
            <Typography variant="h6" sx={{ mt: 0.5 }}>{currency(summary.pagado)}</Typography>
          </Paper>

          <Paper className="summaryCard">
            <Typography variant="caption" color="text.secondary">Pendiente</Typography>
            <Typography variant="h6" sx={{ mt: 0.5 }}>{currency(summary.pendiente)}</Typography>
          </Paper>
        </Box>



        {/* Filtros */}
        <Paper variant="outlined"
          sx={{
            p: 2,
            mb: 2,
            bgcolor: UI.paper,
            border: `1px solid ${UI.border}`,
            boxShadow: 'none',
            borderRadius: 2,
          }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md="auto">
              <Typography variant="subtitle2">Rango:</Typography>
            </Grid>
            <Grid item xs={12} md="auto">
              <ToggleButtonGroup exclusive size="small" value={range} onChange={(_, v) => v && setRange(v)}>
                <ToggleButton value="custom">Seleccionado</ToggleButton>
                <ToggleButton value="mes">Último mes</ToggleButton>
                <ToggleButton value="3m">Últimos 3 meses</ToggleButton>
              </ToggleButtonGroup>
            </Grid>

            {range === "custom" ? (
              <>
                <Grid item xs={12} sm={6} md="auto">
                  <FormControl size="small">
                    <TextField
                      type="date"
                      label="Desde"
                      InputLabelProps={{ shrink: true }}
                      value={desde}
                      onChange={(e) => setDesde(e.target.value)}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md="auto">
                  <FormControl size="small">
                    <TextField
                      type="date"
                      label="Hasta"
                      InputLabelProps={{ shrink: true }}
                      value={hasta}
                      onChange={(e) => setHasta(e.target.value)}
                    />
                  </FormControl>
                </Grid>
              </>
            ) : null}

            <Grid item xs={12} md>
              <TextField
                fullWidth
                size="small"
                placeholder={mode === "producto" ? "Buscar producto..." : "Buscar cliente..."}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md="auto">
              <ToggleButtonGroup
                exclusive
                size="small"
                value={mode}
                onChange={(_, v) => v && setMode(v)}
              >
                <ToggleButton value="producto">Producto</ToggleButton>
                <ToggleButton value="cliente">Cliente</ToggleButton>
              </ToggleButtonGroup>
            </Grid>
          </Grid>
        </Paper>

        {/* Tabla principal */}
        <Paper variant="outlined" sx={{ overflow: "hidden" }}>
          <Table size="small" aria-label="tabla agrupada">
            <TableHead>
              <TableRow
                sx={{
                  bgcolor: UI.head,
                  '& .MuiTableCell-root': { color: '#fff', fontWeight: 700 }
                }}
              >
                <TableCell />
                <TableCell sx={{ fontWeight: 600 }}>{mode === "producto" ? "Producto" : "Cliente"}</TableCell>
                {mode === "producto" ? (
                  <>
                    <TableCell align="right"># Arriendos</TableCell>
                    <TableCell align="right">Total facturado</TableCell>
                    <TableCell align="right">Total pagado</TableCell>
                    <TableCell align="right">Pendiente</TableCell>
                  </>
                ) : (
                  <>
                    <TableCell align="right"># Arriendos</TableCell>
                    <TableCell align="right"># Productos arrendados</TableCell>
                    <TableCell align="right">Total facturado</TableCell>
                    <TableCell align="right">Total pagado</TableCell>
                    <TableCell align="right">Pendiente</TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 2 }}>
                      <CircularProgress size={20} /> <Typography variant="body2">Cargando…</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Alert severity="error">{String(error)}</Alert>
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" sx={{ p: 2 }}>
                      Sin resultados para los filtros seleccionados.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((r) => {
                  const det = detailsMap[r.key] || {};
                  return (
                    <ExpandRow
                      key={r.key}
                      row={r}
                      mode={mode}
                      details={det.rows}
                      isLoading={det.loading}
                      error={det.error}
                      detailsMeta={det.meta}
                      detailsPage={(det.page ?? 1)}
                      detailsPerPage={(det.perPage ?? 10)}
                      onDetailsPageChange={(p) => handleDetailsPageChange(r.key, p)}
                      onDetailsPerPageChange={(pp) => handleDetailsPerPageChange(r.key, pp)}
                      onToggle={(open) => {
                        if (open && !det.rows && !det.loading) {
                          loadDetailsFor(r.key, (det.page ?? 1), (det.perPage ?? 10));
                        }
                      }}
                    />
                  );
                })
              )}
            </TableBody>
          </Table>
        </Paper>

        <Divider sx={{ my: 3 }} />
        <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <Pagination
            page={meta.page || 1}
            count={meta.total_pages || 0}
            onChange={(_, p) => setPage(p)}
            size="small"
          />
          <TextField
            select
            size="small"
            label="Por página"
            value={perPage}
            onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
          >
            {[5, 10, 20, 50].map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
          </TextField>
          <Box sx={{ ml: "auto" }}>
            <Typography variant="caption" color="text.secondary">
              Mostrando {items.length} de {meta.total || 0} {mode}{mode === "cliente" ? "s" : "s"}.
            </Typography>
          </Box>
        </Box>

      </Container>
    </Box>
  );
}
